import type { InvoiceRepository } from "../ports/index.js";
import type { ListInvoicesQuery, ListInvoicesResult } from "./queries.js";

/**
 * Dependencies required for ListInvoices use case
 */
export interface ListInvoicesDependencies {
  readonly invoiceRepository: InvoiceRepository;
}

/**
 * Use case for listing invoices with filtering and pagination
 * 
 * Requirements:
 * - 2.1: Support filtering by invoice number, date range, and total amount range
 * - 2.2: Provide paginated results
 * - 2.5: Return results in consistent format with metadata
 */
export function makeListInvoices(deps: ListInvoicesDependencies) {
  return async (query: ListInvoicesQuery): Promise<ListInvoicesResult> => {
    const { invoiceRepository } = deps;

    // Validate pagination parameters
    if (query.page < 1) {
      throw new Error("Page number must be greater than 0");
    }
    if (query.limit < 1 || query.limit > 100) {
      throw new Error("Limit must be between 1 and 100");
    }

    // Validate date range if provided
    if (query.dateFrom && query.dateTo && query.dateFrom > query.dateTo) {
      throw new Error("dateFrom must be before or equal to dateTo");
    }

    // Validate amount range if provided
    if (query.minTotal !== undefined && query.maxTotal !== undefined && query.minTotal > query.maxTotal) {
      throw new Error("minTotal must be less than or equal to maxTotal");
    }

    // Query invoices from repository
    const paginatedResult = await invoiceRepository.list(query);

    // Transform domain entities to result format
    const result: ListInvoicesResult = {
      items: paginatedResult.items.map(invoice => ({
        invoiceId: invoice.invoiceId,
        invoiceNumber: invoice.invoiceNumber,
        orderId: invoice.orderId,
        customerName: `${invoice.customerSnapshot.firstName} ${invoice.customerSnapshot.lastName}`,
        grandTotal: invoice.financials.grandTotal.toPlainObject(),
        createdOn: invoice.createdOn,
        createdBy: invoice.createdBy,
        itemCount: invoice.getTotalItemCount(),
      })),
      totalCount: paginatedResult.totalCount,
      page: paginatedResult.page,
      limit: paginatedResult.limit,
      hasNextPage: paginatedResult.hasNextPage,
      hasPreviousPage: paginatedResult.hasPreviousPage,
    };

    return result;
  };
}

/**
 * Factory function to create ListInvoices use case with dependencies
 */
export function createListInvoicesUseCase(
  deps: ListInvoicesDependencies
): (query: ListInvoicesQuery) => Promise<ListInvoicesResult> {
  return makeListInvoices(deps);
}